import sessionResumeService from '../features/session/resume/service/sessionResume.service'
import { IPcr, IPcrs } from '../features/pcr/store/pcr.types'
import { NowSQLiteDbService } from './nowSQLiteDb.service'
import { NowIndexDbService } from './nowIndexDb.service'
import { IOrderBy } from './indexDb.service'
import { DatabaseUtilityService } from './databaseUtility.service'
import { startRulesValidation } from '../utilities/runRulesValidation.util'
import { IPcrSummary, IPcrSummaries } from '../features/home/store/home.types'
import { buildPcrSummary } from '../utilities/pcr.util'
import Logger from './logger.service'
import { IndexableType } from 'dexie'
import { store } from '../index'
import { pcrSaved } from '../features/pcrBackup/store/pcrBackup.actions'
import { PcrBackupMetadataService } from '../features/pcrBackup/services/pcrBackupMetadata.service'

export interface IStoragePcr {
  createdBy: string
  createDateTime: number
  pcrId: string | number
  data: string
}

interface CreatedByCount {
  createdBy: string
  recordCount: number
}

export class PcrDocsService {
  private db: NowIndexDbService | NowSQLiteDbService
  private tableName: string
  private database: string

  constructor(database: string) {
    this.database = database
    this.db = DatabaseUtilityService.getInstance(database)
    if (this.db instanceof NowIndexDbService) this.tableName = this.db.pcrDocs.name
    else this.tableName = this.db.pcrDocsTableName
  }

  async getAllPCRDocs(): Promise<IPcrSummaries> {
    await this.logPcrCountsGroupedByCreatedBy()

    const storagePcrs: IStoragePcr[] = await this.db.getAll(this.tableName)

    Logger.info(`Number of storagePcrs created inside service: ${this.database} = ${storagePcrs.length}`)

    const pcrDocSummaries = await this._convertStoragePcrsToPcrSummaries(null, storagePcrs)

    return pcrDocSummaries
  }

  async getPCRDocsPerUser(userId: string): Promise<IPcrSummaries> {
    const orderBy: IOrderBy = {
      orderBy: 'createDateTime',
      asc: false,
    }

    await this.logPcrCountsGroupedByCreatedBy()

    const storagePcrs: IStoragePcr[] = await this.db.getWhereAttributeEquals(
      'createdBy',
      userId,
      this.tableName,
      orderBy
    )

    Logger.info(`Number of storagePcrs createdBy [${userId}] = ${storagePcrs.length}`)

    return this._convertStoragePcrsToPcrSummaries(userId, storagePcrs)
  }

  async getPCR(pcrId: string | number): Promise<IPcr | undefined | null> {
    sessionResumeService.extendSession()
    const storagePcr = await this.db.get(pcrId, this.tableName)
    if (storagePcr) return this._convertStoragePcrToPcr(storagePcr)
  }

  async savePCR(pcr: IPcr): Promise<any> {
    sessionResumeService.extendSession()
    const storagePcr = await this._buildStoragePcr(pcr)
    if (this.db instanceof NowIndexDbService) {
      return this.db.save(storagePcr, this.tableName).then(() => {
        startRulesValidation()
        store.dispatch(pcrSaved(pcr.pcrId))
      })
    } else {
      return this.db.updatePCRDoc(storagePcr).then(() => {
        startRulesValidation()
        store.dispatch(pcrSaved(pcr.pcrId))
      })
    }
  }

  /**
   * Writes the PCR to the DB without dispatching pcrSaved or triggering rules
   * validation. Use only when the caller owns the mutex and must not cause
   * downstream Redux side-effects (e.g. ensurePridFetched during chart load).
   */
  async savePCRSilent(pcr: IPcr): Promise<void> {
    sessionResumeService.extendSession()
    const storagePcr = await this._buildStoragePcr(pcr)
    if (this.db instanceof NowIndexDbService) {
      await this.db.save(storagePcr, this.tableName)
    } else {
      await this.db.updatePCRDoc(storagePcr)
    }
  }

  async createPcrDoc(pcr: IPcrSummary): Promise<IndexableType | IStoragePcr> {
    sessionResumeService.extendSession()
    const storagePcr = await this._buildStoragePcr(pcr)
    if (this.db instanceof NowIndexDbService) {
      return this.db.save(storagePcr, this.tableName)
    } else {
      return this.db.addPCRDoc(storagePcr)
    }
  }

  async deletePcrDoc(pcrId: string | number, userId: string): Promise<void> {
    sessionResumeService.extendSession()
    if (this.db instanceof NowIndexDbService) {
      await this.db.remove(pcrId, this.tableName)
    } else {
      await this.db.removePCRDoc(pcrId, userId)
    }
    new PcrBackupMetadataService(this.database).deleteMetadata(pcrId as string)
  }

  async logPcrCountsGroupedByCreatedBy(): Promise<void> {
    if (this.db instanceof NowSQLiteDbService) {
      const records: CreatedByCount[] = await this.db.getCountsGroupedByCreatedBy(this.tableName)

      if (!records || records.length === 0) {
        Logger.info(`No records exist yet in the ${this.tableName} table`)
        return
      }

      for (const rec of records) {
        Logger.info(`User: ${rec.createdBy} has ${rec.recordCount} PCR records`)
      }
    }
  }

  private async _convertStoragePcrsToPcrSummaries(userId: string | null = null, storagePcrs: IStoragePcr[]) {
    const pcrSummaries: IPcrSummaries = []
    for (const storagePcr of storagePcrs) {
      const pcr = await this._convertStoragePcrToPcr(storagePcr)

      if (pcr) {
        const pcrSummary: IPcrSummary = buildPcrSummary(pcr)
        pcrSummaries.push(pcrSummary)
      } else {
        Logger.warn(
          `PCR record not being added to list since it has data=NULL where pcrId=[${storagePcr.pcrId}], createdBy=[${storagePcr.createdBy}], createDateTime=[${storagePcr.createDateTime}]`
        )
      }
    }
    Logger.info(
      `Number of PCRs converted to PcrSummaries, createdBy [${userId ? userId : 'multiple users'}] in [${
        this.database
      }] service = ${pcrSummaries.length}`
    )

    return pcrSummaries
  }

  private async _convertStoragePcrsToPcrs(storagePcrs: IStoragePcr[]) {
    const pcrs: IPcrs = []
    for (const storagePcr of storagePcrs) {
      const pcr = await this._convertStoragePcrToPcr(storagePcr)
      pcrs.push(pcr)
    }
    return pcrs
  }

  private async _buildStoragePcr(pcr: IPcr | IPcrSummary): Promise<IStoragePcr> {
    return {
      createdBy: pcr.createdBy,
      createDateTime: new Date(pcr.createDateTime).getTime(),
      pcrId: pcr.pcrId,
      data: JSON.stringify(pcr).toString(),
    }
  }

  private async _convertStoragePcrToPcr(storagePcr: IStoragePcr) {
    return JSON.parse(storagePcr.data) as IPcr
  }
}
